import { cilPeople } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CAvatar,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CFormLabel,
  CFormInput,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
} from "@coreui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import ReactSelect from "react-select";
import useTableSort from "src/__custom_hook/useTableSort";
import CustomPagination from "src/components/CustomPagination";
import TableColumnSortIcon from "src/components/Sort/TableColumnSortIcon";
import {
  getBookingOrderLogs,
  getExportableBookingOrder,
  getSearchedBookingOrderList,
} from "src/services/booking.services";
import { getSearchableStoreList } from "src/services/store.services";
import PdfComponent from "../manage-booking-orders/PdfComponent";
import { Document, pdf } from "@react-pdf/renderer";
import ExcelJS from "exceljs";
import MessageModal from "src/components/MessageModal/MessageModal";

const ToReceiveBookingOrders = () => {
  const queryClient = useQueryClient();
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const { sortConfig, setSortConfig, handleClickColumnSort } = useTableSort();

  const [totalPages, setTotalPages] = useState(0);
  const [country, setCountry] = useState(userInfo?.country?._id || null);
  const [bookingOrders, setBookingOrders] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [storeCodeOptions, setStoreCodeOptions] = useState([]);
  const [selectedFromLocation, setSelectedFromLocation] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedModalTitle, setSelectedModalTitle] = useState(null);
  const [visibleMessageModal, setVisibleMessageModal] = useState(false);
  const [count, setCount] = useState(0);

  //get the to location
  const toLocation =
    userInfo && userInfo.userType === "store" && userInfo.location
      ? userInfo.location._id
      : null;

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 10,
    type: "to-store",
    toLocation,
  });

  const { control, register, handleSubmit, reset, setValue, getValues } =
    useForm({
      defaultValues: {
        bookingOrderId: "",
        fromLocation: "",
        sealBag: "",
        status: "",
      },
    });

  const {
    data: bookingOrderQueryData,
    isLoading,
    status,
  } = useQuery({
    queryKey: ["bookingOrder", searchParams],
    queryFn: () => getSearchedBookingOrderList(searchParams),
    enabled: !!toLocation,
  });

  const { data: storeQueryData, isLoading: isLoadingStoreQuery } = useQuery({
    queryKey: ["store-location"],
    queryFn: () => getSearchableStoreList({ country }),
    enabled: true,
  });

  const GetBookingOrderLogsMutation = useMutation({
    mutationKey: ["booking-orders-log"],
    mutationFn: (id) => getBookingOrderLogs(id),
    onMutate: () => {
      toast.loading("Getting history....");
    },
    onSuccess: (response) => {
      toast.dismiss();
      const historyObj = {
        type: "history",
        data: response.data.reverse(),
      };
      setSelectedModalTitle("Booking Order History");
      setSelectedMessage(historyObj);
      setVisibleMessageModal(true);
    },
    onError: (error) => {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Something went wrong!!!");
    },
    onSettled: () => {
      queryClient.invalidateQueries(["booking-orders-log"]);
    },
  });

  const getStatusList = () => {
    const statusObj = [
      { name: "All", value: "" },
      { name: "CDT", value: "cvit" },
      { name: "Assigned", value: "assigned" },
      { name: "Accepted", value: "driver" },
    ];
    let options = [];
    statusObj.forEach((element) => {
      const option = { value: `${element.value}`, label: `${element.name}` };
      options.push(option);
    });
    setStatusOptions(options);
  };

  const onSearchFromSubmit = (data) => {
    const transFormData = transformData(data);
    console.log(transFormData);
    setSearchParams((prev) => ({ ...prev, ...transFormData }));
  };

  const resetSearchForm = () => {
    reset();
    setSelectedFromLocation(null);
    const defaultValues = getValues();
    setSearchParams((prev) => ({ ...prev, ...defaultValues }));
  };

  const handChangeFromLocation = (selectedOption) => {
    if (selectedOption) {
      setSelectedFromLocation(selectedOption.value);
      setValue("fromLocation", selectedOption.value);
    } else {
      setValue("fromLocation", null);
      setSelectedFromLocation(null);
    }
  };

  const transformData = (obj) => {
    const transformedObj = {}; // Create a new object to hold the transformed data

    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        // Check if the object has both 'label' and 'value'
        if (
          obj[key].hasOwnProperty("label") &&
          obj[key].hasOwnProperty("value")
        ) {
          // Set the key to the value
          transformedObj[key] = obj[key].value;
        } else {
          // Recursively call the function for nested objects
          transformedObj[key] = transformData(obj[key]);
        }
      } else {
        // If it's not an object, just copy the value
        transformedObj[key] = obj[key];
      }
    }

    if (sortConfig && sortConfig.length > 0) {
      transformData.sort = sortConfig
        .map((config) => `${config.key}:${config.direction}`)
        .join(",");
    }

    return transformedObj; // Return the transformed object
  };

  const fetchApiDataAndDownloadPDF = async () => {
    const formValues = getValues();
    const processedData = transformData(formValues);

    let params = {
      ...processedData,
      toLocation: searchParams.toLocation,
      type: searchParams.type,
    };
    toast.loading("Preparing the pdf...");

    getExportableBookingOrder(params)
      .then(async (response) => {
        let result = response.data;
        let exportableData = [];
        result.forEach((element) => {
          let obj = {
            bookingOrderId: element.bookingOrderId,
            transferOrderNo: element.transferOrderNo,
            sealBag: element?.sealBag?.serialNo,
            fromLocation: element?.fromLocation?.name,
            toLocation: element?.toLocation?.name,
            tripRoute: element?.tripRoute?.name,
            status: element.status,
            priority: element.priority ? element.priority : "normal",
          };
          exportableData.push(obj);
        });
        if (exportableData.length <= 0) {
          toast.dismiss();
          toast.error("There is not data to export");
          return;
        }
        generatePDF(exportableData, response.filteredDetails);
        toast.dismiss();
      })
      .catch((error) => {
        toast.dismiss();
        console.error(error);
      });
  };

  const generatePDF = async (data, filteredData) => {
    const MyDocument = () => (
      <Document>
        <PdfComponent data={data} filteredData={filteredData} />
      </Document>
    );

    const pdfBlob = await pdf(<MyDocument />).toBlob();
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "to-receive-booking-orders.pdf";
    link.click();
  };

  const exportToExcel = async () => {
    toast.loading("Preparing downloads...");
    const formValues = getValues();
    const processedData = transformData(formValues);

    let params = {
      ...processedData,
      toLocation: searchParams.toLocation,
      type: searchParams.type,
    };

    getExportableBookingOrder(params)
      .then(async (response) => {
        let result = response.data;
        let filteredDetails = response.filteredDetails;

        // Create a new workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Sheet1");

        // Merge cells for the header
        worksheet.mergeCells("A1:J1"); // Merge the first row across 21 columns (A to T)

        // Add and style the header
        const headerCell = worksheet.getCell("A1");
        headerCell.value = "Damas";
        headerCell.font = {
          bold: true,
          color: { argb: "00000000" },
          size: 32,
        };
        headerCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFFFF" },
        };
        headerCell.alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getRow(1).height = headerCell.font.size * 1.2;

        // define the next row
        let nextRow = 2;

        if (filteredDetails && Array.isArray(filteredDetails)) {
          const relevantKeys = filteredDetails.filter((item) =>
            ["status", "tripRoute"].includes(item.key)
          );

          if (relevantKeys.length > 0) {
            // Handle case with two columns
            if (relevantKeys.length === 2) {
              worksheet.mergeCells("A" + nextRow + ":E" + nextRow); // Merge first half
              worksheet.mergeCells("F" + nextRow + ":J" + nextRow); // Merge second half

              const headerCellA = worksheet.getCell("A" + nextRow);
              headerCellA.value = `${relevantKeys[0].key}: ${relevantKeys[0].value}`; // Set value for first column
              headerCellA.font = {
                bold: true,
                color: { argb: "00000000" },
                size: 20,
                family: "Cambria",
              };

              headerCellA.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFFFF" },
              };
              headerCellA.text.charAt(0).toUpperCase();

              const headerCellF = worksheet.getCell("F" + nextRow);
              // headerCellF.value = `${relevantKeys[0].key}: ${relevantKeys[0].value}`; // Set value for first column ${relevantKeys[1].value}`; // Set value for first column

              headerCellF.value = `${relevantKeys[0].key}: ${relevantKeys[0].value}`;

              headerCellF.font = {
                bold: true,
                color: { argb: "00000000" },
                size: 14,
                family: "Calibri",
              };
              headerCellF.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFFFF" },
              };
              headerCellF.text.charAt(0).toUpperCase();

              worksheet.getRow(nextRow).height = headerCellA.font.size * 1.2; // Adjust row height
              // worksheet.getRow(nextRow).border = {
              //   top: { style: "medium" },
              //   bottom: { style: "medium" },
              // };

              // Increment nextRow for subsequent data
            } else {
              // Handle case with more than two columns (merge all)
              worksheet.mergeCells("A" + nextRow + ":J" + nextRow);
              const headerCellA = worksheet.getCell("A" + nextRow);
              headerCellA.value = `${relevantKeys[0].key}: ${relevantKeys[0].value}`; // Or dynamically construct a header based on all keys, first char uppercase

              console.log(`${relevantKeys[0].key}: ${relevantKeys[0].value}`);

              headerCellA.font = {
                bold: true,
                color: { argb: "00000000" },
                size: 14,
                family: "Calibri",
              };
              headerCellA.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFFFF" },
              };

              headerCellA.text.charAt(0).toUpperCase();
              worksheet.getRow(nextRow).height = headerCellA.font.size * 1.2;
              // worksheet.getRow(nextRow).border = {
              //   top: { style: "medium" },
              //   bottom: { style: "medium" },
              // };
            }
            nextRow++;
          }
        }

        // Add and style the column headers
        worksheet.addRow([
          "Sl.",
          "Order ID",
          "T.O/ST",
          "Bag No.",
          "Booking",
          "From",
          "To",
          "Name",
          "Signature",
          "E Code",
        ]);
        // worksheet.addRow([
        //   "Sl.",
        //   "OrderId",
        //   "FromLocation",
        //   "ToLocation",
        //   "Pick Date",
        //   "Pickup Time",
        //   "SealBag",
        //   "Remarks",
        //   "TransferOrderNo",
        //   "Created Date",
        //   "Created By",
        //   "Accept or Rejected By",
        //   "Trip Created By",
        //   "Vechicle Id",
        //   "Route Name",
        //   "Route Code",
        //   "Trip Driver",
        //   "Status",
        //   "Priority",
        //   "Country",
        // ]);

        worksheet.columns.forEach((column) => {
          if (column.letter === "A") {
            column.width = 5;
          }
          if (/^[B-T]$/.test(column.letter)) {
            // Set width for column B to R
            column.width = 25;
          }
        });

        const secondTitlRow = worksheet.getRow(nextRow);
        secondTitlRow.eachCell((cell) => {
          cell.font = { bold: true, family: "Calibri", size: 11 };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        // Add data

        result.forEach((element, index) => {
          const rowData = [
            index + 1,
            element.bookingOrderId,
            element.transferOrderNo,
            element?.sealBag?.serialNo,
            element.status,
            element?.fromLocation?.name,
            element?.toLocation?.name,
            "",
            "",
            "",
          ];
          worksheet.addRow(rowData);
        });

        // result.forEach((element, index) => {
        //   const rowData = [
        //     index + 1,
        //     element.bookingOrderId,
        //     element?.fromLocation?.name,
        //     element?.toLocation?.name,
        //     element?.pickupDate,
        //     element?.pickupTime,
        //     element?.sealBag?.serialNo,
        //     element?.remarks,
        //     element.transferOrderNo,
        //     moment(element.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        //     element.createdBy
        //       ? `${element?.createdBy?.firstName} ${element?.createdBy?.lastName}`
        //       : "",
        //     element.orderAcceptRejectById
        //       ? `${element?.orderAcceptRejectById?.firstName} ${element?.orderAcceptRejectById?.lastName}`
        //       : "",
        //     element?.tripAssignedBy
        //       ? `${element?.tripAssignedBy?.firstName} ${element?.tripAssignedBy?.lastName}`
        //       : "",
        //     element?.tripVehicleId,
        //     element?.tripRoute?.name,
        //     element?.tripRoute?.routeCode,
        //     element?.tripAssignedTo
        //       ? `${element?.tripAssignedTo?.firstName} ${element?.tripAssignedTo?.lastName}`
        //       : "",
        //     element.status,
        //     element.priority ? element.priority : "normal",
        //     element?.country?.name,
        //   ];
        //   worksheet.addRow(rowData);
        // });

        // Add borders to the data cells
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            // cell.font = {
            //   family: "Cambria",
            //   bold: false,
            //   size: 12,
            // };
          });
        });

        // Write workbook to a buffer
        const buffer = await workbook.xlsx.writeBuffer();

        // Save the buffer as a file
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, "Booking-Order.xlsx");

        toast.dismiss();
      })
      .catch((error) => {
        toast.dismiss();
        console.error(error);
      });
  };

  // handle pagination
  const handlePageChange = (newPage) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }));
  };

  const handleHistoryDetails = async (e, itemId) => {
    e.stopPropagation();
    GetBookingOrderLogsMutation.mutate(itemId);
  };

  useEffect(() => {
    const sortString = sortConfig
      .map((config) => `${config.key}:${config.direction}`)
      .join(",");
    setSearchParams((prev) => ({ ...prev, sort: sortString }));
  }, [sortConfig]);

  useEffect(() => {
    if (!isLoading && bookingOrderQueryData && status === "success") {
      const { data, success, count } = bookingOrderQueryData;

      if (success) {
        setCount(count);
        const totalPagesCount = Math.ceil(count / searchParams.limit);
        setTotalPages(totalPagesCount);
        setBookingOrders(data);
      }
    }
  }, [bookingOrderQueryData, isLoading]);

  useEffect(() => {
    if (!isLoadingStoreQuery && storeQueryData) {
      const dataList = storeQueryData.data;

      let storeList = [];
      let storeCodeList = [];

      dataList.forEach((element) => {
        const codeOption = {
          value: `${element._id}`,
          label: `${element.locationCode}`,
        };
        const storeOption = {
          value: `${element._id}`,
          label: `${element.name}`,
        };
        storeList.push(storeOption);
        storeCodeList.push(codeOption);
      });

      setStoreOptions(storeList);
      setStoreCodeOptions(storeCodeList);
    }
  }, [storeQueryData, isLoadingStoreQuery]);

  useEffect(() => {
    getStatusList();
  }, []);

  return (
    <div className="d-flex flex-column gap-4">
      <CCard>
        <CCardBody>
          <form onSubmit={handleSubmit(onSearchFromSubmit)}>
            <CRow
              xs={{
                gutter: 4,
                cols: 4,
              }}
            >
              <CCol>
                <CFormLabel htmlFor="bookingOrderId">
                  Booking Order ID:
                </CFormLabel>
                <CFormInput
                  type="text"
                  name="bookingOrderId"
                  placeholder="example:DD001"
                  {...register("bookingOrderId")}
                />
              </CCol>
              <CCol>
                <CRow>
                  <CCol sm={5}>
                    <CFormLabel htmlFor="fromLocationCode">
                      From Location Code:
                    </CFormLabel>

                    <ReactSelect
                      options={storeCodeOptions}
                      value={
                        storeCodeOptions.find(
                          (item) => item.value === selectedFromLocation
                        ) || ""
                      }
                      onChange={handChangeFromLocation}
                      isClearable={true}
                    />
                  </CCol>
                  <CCol sm={7}>
                    <CFormLabel htmlFor="fromLocation">
                      From Location Name:
                    </CFormLabel>

                    <ReactSelect
                      options={storeOptions}
                      value={
                        storeOptions.find(
                          (item) => item.value === selectedFromLocation
                        ) || ""
                      }
                      onChange={handChangeFromLocation}
                      isClearable={true}
                    />
                  </CCol>
                </CRow>
              </CCol>
              <CCol>
                <CFormLabel htmlFor="sealBagNumber">Seal/Bag No.</CFormLabel>
                <CFormInput
                  id="sealBagNumber"
                  name="sealBag"
                  type="text"
                  placeholder="1900052"
                  {...register("sealBag")}
                />
              </CCol>
              <CCol>
                <CFormLabel htmlFor="bookingOrderStatus">Status:</CFormLabel>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      id="bookingOrderStatus"
                      name="status"
                      options={statusOptions}
                      isClearable={true}
                      {...field}
                    />
                  )}
                />
              </CCol>
            </CRow>
            <div className="d-flex justify-content-end mt-3">
              <CButton type="submit" color="primary" className="px-5 py-2 me-3">
                Submit
              </CButton>
              <CButton
                type="button"
                className="px-5 py-2 ms-3 btn-oliveGreen"
                onClick={resetSearchForm}
              >
                Reset
              </CButton>
            </div>
          </form>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <CRow>
            <CCol sm={7}>
              <h4 id="traffic" className="card-title mb-0">
                To Receive Booking Orders - Total: {count}
              </h4>
              {/* <div className="small text-medium-emphasis">January - July 2021</div> */}
            </CCol>
            <CCol sm={5} className="d-none d-md-block">
              <div className="d-flex justify-content-end gap-4">
                <CButtonGroup
                  role="group"
                  aria-label="Button group with nested dropdown"
                  style={{ marginRight: "10px" }}
                >
                  <CDropdown variant="btn-group">
                    <CDropdownToggle color="primary">Export</CDropdownToggle>
                    <CDropdownMenu>
                      <CDropdownItem
                        onClick={() => fetchApiDataAndDownloadPDF()}
                      >
                        as PDF
                      </CDropdownItem>
                      <CDropdownItem onClick={() => exportToExcel()}>
                        as Excel
                      </CDropdownItem>
                    </CDropdownMenu>
                  </CDropdown>
                </CButtonGroup>
              </div>
            </CCol>
          </CRow>
          <br />
          <CTable align="middle" className="mb-0 border" hover responsive>
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell className="text-center">
                  Status
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  History
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  <CIcon icon={cilPeople} />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("bookingOrderId")}
                  style={{ cursor: "pointer" }}
                >
                  Booking Order ID
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "bookingOrderId" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("transferOrderNo")}
                  style={{ cursor: "pointer" }}
                >
                  Transfer Order No.
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "transferOrderNo" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("sealBag")}
                  style={{ cursor: "pointer" }}
                >
                  Seal/Bag No.
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "sealBag" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("fromLocation")}
                  style={{ cursor: "pointer" }}
                >
                  From Location{" "}
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "fromLocation" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("toLocation")}
                  style={{ cursor: "pointer" }}
                >
                  To Location{" "}
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "toLocation" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("type")}
                  style={{ cursor: "pointer" }}
                >
                  Type{" "}
                  <TableColumnSortIcon {...{ sortConfig, dataKey: "type" }} />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("unit")}
                  style={{ cursor: "pointer" }}
                >
                  Unit
                  <TableColumnSortIcon {...{ sortConfig, dataKey: "unit" }} />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("tripId")}
                  style={{ cursor: "pointer" }}
                >
                  Trip Name{" "}
                  <TableColumnSortIcon {...{ sortConfig, dataKey: "tripId" }} />
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("tripRoute")}
                  style={{ cursor: "pointer" }}
                >
                  Route Name{" "}
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "tripRoute" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  Priority
                </CTableHeaderCell>
                <CTableHeaderCell
                  className="text-center"
                  onClick={() => handleClickColumnSort("pickupDateTime")}
                  style={{ cursor: "pointer" }}
                >
                  Pick Up Date{" "}
                  <TableColumnSortIcon
                    {...{ sortConfig, dataKey: "pickupDateTime" }}
                  />
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  Remarks
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {isLoading ? (
                <CTableRow className="text-center">
                  <CTableDataCell colSpan="20">Loading...</CTableDataCell>
                </CTableRow>
              ) : bookingOrders && bookingOrders.length == 0 ? (
                <CTableRow className="text-center">
                  <CTableDataCell colSpan="16">
                    No data available
                  </CTableDataCell>
                </CTableRow>
              ) : (
                bookingOrders.map((item, index) => (
                  <CTableRow
                    v-for="item in tableItems"
                    key={index}
                    data-id={item._id}
                    data-status={item.status}
                    className={
                      item.priority == "emergency" ? "emergency-color" : ""
                    }
                  >
                    <CTableDataCell className="text-center">
                      {item.status === "New" ? (
                        <CButton
                          color="secondary"
                          className="text-capitalize"
                          size="sm"
                          style={{ color: "white" }}
                        >
                          {item.status}
                        </CButton>
                      ) : (
                        ""
                      )}
                      {item.status === "rejected" ? (
                        <CButton
                          color="danger"
                          className="text-capitalize"
                          size="sm"
                          style={{ color: "white" }}
                          onClick={() =>
                            handleRejectDetails(item?.rejectReason)
                          }
                        >
                          {item.status}
                        </CButton>
                      ) : (
                        ""
                      )}
                      {item.status === "driver" ? (
                        <CButton
                          color="purple"
                          className="text-capitalize"
                          size="sm"
                          onClick={() =>
                            handleDriverDetails(
                              item?.tripAssignedTo,
                              item?.tripTagId
                              // item?.tripVehicleId
                            )
                          }
                        >
                          Accepted
                        </CButton>
                      ) : (
                        ""
                      )}

                      {item.status === "cvit" && (
                        <CButton
                          color="pink"
                          size="sm"
                          className="text-capitalize"
                        >
                          CDT
                        </CButton>
                      )}

                      {item.status === "assigned" ? (
                        <CButton
                          color="gray"
                          className="text-capitalize"
                          size="sm"
                          onClick={() =>
                            handleDriverDetails(
                              item?.tripAssignedTo,
                              // item?.tripVehicleId
                              item?.tripTagId
                            )
                          }
                        >
                          {item.status}
                        </CButton>
                      ) : (
                        ""
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-center text-capitalize">
                      <CButton
                        color="primary"
                        className="text-capitalize"
                        size="sm"
                        style={{ color: "white" }}
                        onClick={(e) => handleHistoryDetails(e, item?._id)}
                      >
                        Show History
                      </CButton>
                    </CTableDataCell>

                    <CTableDataCell className="text-center">
                      {item.status == "cvit" ? (
                        <CAvatar
                          size="md"
                          src={
                            item?.orderAcceptRejectBy?.avatar
                              ? item.orderAcceptRejectBy.avatar
                              : demoAvatar
                          }
                          status="success"
                        />
                      ) : (
                        ""
                      )}
                      {item.status == "rejected" ? (
                        <CAvatar
                          size="md"
                          src={
                            item?.orderAcceptRejectBy?.avatar
                              ? item.orderAcceptRejectBy.avatar
                              : demoAvatar
                          }
                          status="success"
                        />
                      ) : (
                        ""
                      )}
                      {item.status == "assigned" ? (
                        <CAvatar
                          size="md"
                          src={
                            item?.tripAssignedBy?.avatar
                              ? item.tripAssignedBy.avatar
                              : demoAvatar
                          }
                          status="success"
                        />
                      ) : (
                        ""
                      )}
                      {item.status == "driver" ? (
                        <CAvatar
                          size="md"
                          src={
                            item?.tripAssignedTo?.avatar
                              ? item.tripAssignedTo.avatar
                              : demoAvatar
                          }
                          status="success"
                        />
                      ) : (
                        ""
                      )}
                      {item.status == "delivered" ? (
                        <CAvatar
                          size="md"
                          src={
                            item?.tripAssignedTo?.avatar
                              ? item.tripAssignedTo.avatar
                              : demoAvatar
                          }
                          status="success"
                        />
                      ) : (
                        ""
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div>{item.bookingOrderId}</div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div>{item.transferOrderNo}</div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div>{item.sealBag?.serialNo}</div>
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item.fromLocation
                        ? `${item.fromLocation.name} (${item.fromLocation.locationCode})`
                        : ""}
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item.toLocation
                        ? `${item.toLocation.name} (${item?.toLocation?.locationCode})`
                        : ""}
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item.type ? item.type.name : ""}
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item.unit ? item.unit.name : ""}
                    </CTableDataCell>
                    <CTableDataCell>{item?.tripId?.tripName}</CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item?.tripRoute?.name}
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {item.priority}
                    </CTableDataCell>
                    <CTableDataCell className="text-capitalize">
                      {moment.utc(item.pickupDateTime).format("DD/MM/YYYY hh:mm A")}
                    </CTableDataCell>

                    <CTableDataCell className="text-capitalize">
                      {item?.remarks}
                    </CTableDataCell>
                  </CTableRow>
                ))
              )}
            </CTableBody>
          </CTable>
          <br />
          <CustomPagination
            currentPage={searchParams.page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </CCardBody>
      </CCard>

      <MessageModal
        visible={visibleMessageModal}
        setVisibleMessageModal={setVisibleMessageModal}
        message={selectedMessage}
        title={selectedModalTitle}
        // addToast={addToast}
      />
    </div>
  );
};

export default ToReceiveBookingOrders;
